/**
 * Trust Operations Center — the executive God View.
 *
 * Composes the six sub-scores that already exist across the platform
 * (identity_verification, credential_health, rule_execution,
 * chain_of_custody, compliance_completeness, broker_confirmations)
 * into a single Platform Trust Score.
 *
 * Data source: GET /api/admin/trust-ops/{overview,rule-packages,compliance-gaps,incidents}
 * Backend service: core/trust_ops.py — the algorithm lives there.
 *
 * This is intentionally read-only. It does not create new state; it exposes
 * what the trust engines are already capturing.
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { apiClient } from "../../lib/api";
import { ShieldCheck, Warning, ChartLineUp, Sparkle, Clock, ArrowRight, Package, Broadcast, ClipboardText } from "@phosphor-icons/react";
import { BreathingDot, PulseOnChange } from "../../components/live/LiveWorld";

const REFRESH_MS = 30_000;

// Colors match the MedexOVA brand palette (bone / forest / brandblue).
const C = {
  bone: "#F4F1EA",
  ink: "#0F172A",
  slate: "#475569",
  slateDim: "#94A3B8",
  forest: "#2B4C3B",
  brandblue: "#3B82F6",
  amber: "#B45309",
  red: "#B91C1C",
  green: "#047857",
  cardBorder: "#E2E0D8",
};

const gradeColor = (grade) => {
  if (grade === "A") return C.green;
  if (grade === "B") return "#0EA5E9";
  if (grade === "C") return C.amber;
  return C.red;
};

const scoreColor = (s) => (s >= 90 ? C.green : s >= 75 ? "#0EA5E9" : s >= 60 ? C.amber : C.red);

const SUB_LABELS = {
  identity_verification: {
    label: "Identity verification",
    desc: "MedexOVA Auth coverage across the fleet",
    help: {
      what: "The share of drivers scored MedexOVA Verified (Credential Score ≥ 80) via face enrollment, license verification, background check, and insurance-on-file.",
      how: "= (drivers with Credential Score ≥ 80) ÷ (total drivers) × 100",
      improve: "Every completed MedexOVA Auth enrollment lifts this number. Filter below to see who's still not Verified and text or email them your onboarding link.",
      action: { label: "Show drivers not yet Verified →", href: "/admin/drivers?filter=below_verified" },
    },
  },
  credential_health: {
    label: "Credential health",
    desc: "No-expired-credential rate",
    help: {
      what: "The share of active drivers whose license, insurance, background check, and training documents have not expired.",
      how: "= (drivers with zero expired credentials) ÷ (total drivers) × 100",
      improve: "Renewing an expired document lifts this. The banner on Credentialing shows which drivers to chase first.",
      action: { label: "Show expired credentials →", href: "/admin/credentialing-hub?filter=expired" },
    },
  },
  rule_execution: {
    label: "Rule execution",
    desc: "Declared verifications actually captured",
    help: {
      what: "Of completed trips in the last 7 days, the share where every verification the Rule Package declared (face match, signature, etc.) actually fired.",
      how: "For each trip, compare `workflow.verifications` count to the `verification.*` events in activity_log. A trip 'passes' when events ≥ declared.",
      improve: "Active Compliance Gaps (below on this panel) already lists in-flight trips missing declared verifications. Click any row to jump to Live Ops with the trip pre-focused.",
    },
  },
  chain_of_custody: {
    label: "Chain of custody",
    desc: "Both tamper seals captured (courier)",
    help: {
      what: "For courier trips whose Rule Package requires a chain-of-custody report, the share that captured both the pickup and drop-off tamper-seal photos.",
      how: "= (courier trips with both `pickup_seal` AND `dropoff_seal` on the trip doc) ÷ (courier trips) × 100",
      improve: "Drivers must tap 'Take tamper-seal photo' on the Beacon at both ceremonies. Missing captures surface in the Active Compliance Gaps section below.",
    },
  },
  compliance_completeness: {
    label: "Compliance packet",
    desc: "POD signature + audit trail present",
    help: {
      what: "Of completed trips in the last 7 days, the share that carry BOTH a POD signature and at least one activity_log entry — the minimum for a payer-ready compliance packet.",
      how: "= (completed trips with `signature` set AND ≥1 activity_log entry) ÷ (completed trips) × 100",
      improve: "Confirm the Beacon's POD-capture step runs before COMPLETE. Un-signed trips need a manual dispatcher signature or trip re-open.",
    },
  },
  broker_confirmations: {
    label: "Broker POD-back",
    desc: "Broker trips with confirmed webhook",
    help: {
      what: "For broker-sourced trips (Kaizen/MedexOva/etc.) completed in the last 30 days, the share where MedexOVA's POD-back webhook was acknowledged by the broker.",
      how: "= (broker trips with a `broker_confirmations` row in status sent/confirmed/acknowledged) ÷ (completed broker trips) × 100",
      improve: "A growing gap usually means a broker changed their webhook endpoint. Retry from the filtered view to send the POD-back again.",
      action: { label: "Show unconfirmed POD-backs →", href: "/admin/broker-conf?filter=unconfirmed" },
    },
  },
  va_efficiency: {
    label: "VA efficiency",
    desc: "Ingested offers actually placed with a provider",
    help: {
      what: "The human-in-the-loop score. Of offers ingested from Kaizen (and other brokers) in the last 7 days, the share the VA actually placed with a TP or independent driver.",
      how: "= (trips with an assigned org or driver) ÷ (offers ingested) × 100. A silent drop (offer expired without a VA decision) counts against this score.",
      improve: "Watch the VA Ops section below for silent drops. If they're piling up, the VA is either overwhelmed, off-shift, or missing the alert. The pending-queue alarm now fires on every screen with haptic + sound.",
      action: { label: "Show pending trips →", href: "/admin/live-ops?filter=pending" },
    },
  },
};

// Compact "?" tooltip — no library, no popover framework. Just a controlled
// hover/click card that stays put until you move away or click again.
function HelpTip({ help, testid }) {
  const [open, setOpen] = React.useState(false);
  if (!help) return null;
  return (
    <span
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      style={{ position: "relative", display: "inline-flex", marginLeft: 6 }}
    >
      <button
        type="button"
        aria-label="More info"
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        style={{
          width: 16, height: 16, borderRadius: "50%", border: `1px solid ${C.slateDim}`,
          background: "#fff", color: C.slate, fontSize: 10, fontWeight: 800,
          cursor: "help", padding: 0, lineHeight: "14px", display: "inline-flex",
          alignItems: "center", justifyContent: "center",
        }}
      >?</button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20,
          width: 320, background: C.ink, color: "#fff", borderRadius: 8,
          padding: 12, boxShadow: "0 8px 24px rgba(15,23,42,0.24)", fontWeight: 400,
          textTransform: "none", letterSpacing: 0,
        }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.65, fontWeight: 800, marginBottom: 4 }}>What it measures</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>{help.what}</div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.65, fontWeight: 800, marginBottom: 4 }}>How it&apos;s calculated</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 8, fontFamily: "ui-monospace, Menlo, monospace" }}>{help.how}</div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.65, fontWeight: 800, marginBottom: 4, color: "#86EFAC" }}>How to move it up</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "#DCFCE7" }}>{help.improve}</div>
          {help.action && help.action.href && (
            <a
              href={help.action.href}
              data-testid={testid ? `${testid}-action` : "trust-ops-help-action"}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "inline-flex", marginTop: 10, padding: "6px 10px",
                borderRadius: 6, background: "#86EFAC", color: "#052E1A",
                fontSize: 11, fontWeight: 800, textDecoration: "none",
                textTransform: "uppercase", letterSpacing: "0.08em",
              }}
            >
              {help.action.label}
            </a>
          )}
        </div>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function TrustScoreHero({ overview }) {
  if (!overview) {
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 24, marginBottom: 16 }} data-testid="trust-ops-hero-loading">
        <div style={{ fontSize: 12, color: C.slate }}>Loading Platform Trust Score…</div>
      </div>
    );
  }
  const s = overview.trust_score ?? 0;
  const g = overview.grade || "F";
  const col = gradeColor(g);
  const heroHelp = {
    what: "The single number that measures whether the Verified Transportation Infrastructure is doing its job. Rolls up six sub-dimensions into one 0–100 score with a letter grade.",
    how: "= Σ (sub_score × weight)  where weights are: identity_verification 25% · credential_health 20% · rule_execution 20% · chain_of_custody 15% · compliance_completeness 10% · broker_confirmations 10%",
    improve: "Read the sub-scores below. The lowest-weighted sub-score with the biggest gap is the highest-leverage lift. Each card has its own '?' with a specific improvement action.",
  };
  return (
    <div
      data-testid="trust-ops-hero"
      style={{ background: `linear-gradient(135deg, ${C.forest} 0%, ${C.ink} 100%)`, borderRadius: 14, padding: 24, marginBottom: 16, color: "#fff" }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.16em", opacity: 0.7, marginBottom: 8, fontWeight: 700, display: "inline-flex", alignItems: "center" }}>
            Platform Trust Score
            <span style={{ marginLeft: 6 }}><HelpTip help={heroHelp} testid="trust-ops-hero-help" /></span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
            <PulseOnChange value={s.toFixed(1)} color="#10B981">
              <div style={{ fontSize: 72, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", display: "inline-flex", alignItems: "baseline", gap: 12 }} data-testid="trust-ops-score">
                <span style={{ display: "inline-block", position: "relative", top: -18 }}><BreathingDot color="#10B981" size={10}/></span>
                {s.toFixed(1)}
              </div>
            </PulseOnChange>
            <div style={{ padding: "6px 14px", borderRadius: 8, background: col, color: "#fff", fontSize: 22, fontWeight: 800, letterSpacing: "0.02em" }} data-testid="trust-ops-grade">{g}</div>
          </div>
          <div style={{ marginTop: 10, fontSize: 13, opacity: 0.75, maxWidth: 560, lineHeight: 1.55 }}>
            Weighted rollup of identity verification, credential health, rule execution, chain-of-custody, compliance completeness, and broker confirmations. This is the flagship metric of the Verified Transportation Infrastructure.
          </div>
        </div>
        <div style={{ textAlign: "right", opacity: 0.8, fontSize: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginBottom: 4 }}>
            <Clock size={12} /> Live · refreshes every {REFRESH_MS / 1000}s
          </div>
          {overview.computed_at && (
            <div>Computed {new Date(overview.computed_at).toLocaleTimeString()}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubScoreGrid({ overview }) {
  const sub = overview?.sub_scores || {};
  const items = Object.entries(SUB_LABELS).map(([key, meta]) => ({ key, ...meta, ...(sub[key] || {}) }));
  return (
    <div data-testid="trust-ops-subscores" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 16 }}>
      {items.map(item => {
        const s = item.score ?? 0;
        const col = scoreColor(s);
        const w = ((item.weight || 0) * 100).toFixed(0);
        return (
          <div key={item.key} data-testid={`trust-ops-subscore-${item.key}`}
            style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                  {item.label}
                  <HelpTip help={item.help} testid={`trust-ops-subscore-help-${item.key}`} />
                </div>
                <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>{item.desc}</div>
              </div>
              <div style={{ fontSize: 10, color: C.slateDim, background: C.bone, padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>{w}%</div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: col, letterSpacing: "-0.02em" }}>{s.toFixed(1)}</div>
              <div style={{ fontSize: 12, color: C.slate, fontWeight: 500 }}>/ 100</div>
            </div>
            <div style={{ marginTop: 8, height: 6, borderRadius: 3, background: "#F1F5F9", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min(100, s)}%`, background: col }} />
            </div>
            {item.message && (
              <div style={{ fontSize: 11, color: C.slateDim, marginTop: 8, lineHeight: 1.4 }}>{item.message}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RulePackageHeatmap({ telemetry }) {
  const pkgs = telemetry?.packages || [];
  return (
    <div data-testid="trust-ops-rule-telemetry" style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Package size={16} weight="bold" color={C.forest} />
          <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Rule Package activity</div>
          <div style={{ fontSize: 11, color: C.slateDim }}>· last {telemetry?.window_days ?? 7} days</div>
        </div>
      </div>
      {pkgs.length === 0 && <div style={{ fontSize: 12, color: C.slate }}>No Rule Packages loaded.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {pkgs.map(p => {
          const isDead = p.is_dead_path;
          return (
            <div key={p.package_id} data-testid={`trust-ops-pkg-${p.package_id}`}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", borderRadius: 8, background: isDead ? "#F8FAFC" : C.bone, border: isDead ? `1px dashed ${C.cardBorder}` : `1px solid ${C.cardBorder}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, display: "flex", alignItems: "center", gap: 8 }}>
                  {p.package_name}
                  {isDead && <span style={{ fontSize: 10, color: C.amber, background: "#FEF3C7", padding: "2px 6px", borderRadius: 4, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Dead path</span>}
                </div>
                <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>
                  {p.package_id} · priority {p.priority}
                </div>
              </div>
              <div style={{ textAlign: "right", minWidth: 140 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: isDead ? C.slateDim : C.forest, letterSpacing: "-0.02em" }}>
                  {p.trips_total}
                </div>
                <div style={{ fontSize: 10, color: C.slate }}>
                  trips · {p.trips_completed} completed
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComplianceGapsList({ gaps, onOpenTrip }) {
  const rows = gaps?.gaps || [];
  return (
    <div data-testid="trust-ops-gaps" style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Warning size={16} weight="bold" color={C.amber} />
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Active compliance gaps</div>
        <div style={{ fontSize: 11, color: C.slateDim }}>· {rows.length} in-flight trip{rows.length === 1 ? "" : "s"} missing declared verifications</div>
      </div>
      {rows.length === 0 && (
        <div style={{ padding: "12px 0", fontSize: 12, color: C.green, display: "flex", alignItems: "center", gap: 6 }}>
          <ShieldCheck size={14} weight="fill" /> All in-flight trips are executing their Rule Packages. Nothing to flag.
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.slice(0, 12).map(g => (
            <button
              key={g.trip_id}
              data-testid={`trust-ops-gap-${g.trip_id}`}
              onClick={() => onOpenTrip?.(g.trip_id)}
              style={{
                textAlign: "left", background: g.severity === "high" ? "#FEF2F2" : "#FEF3C7",
                border: `1px solid ${g.severity === "high" ? "#FCA5A5" : "#FCD34D"}`,
                borderRadius: 8, padding: "10px 12px", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>
                  Trip {g.trip_id} · {g.package_id || "no package"} · {g.status}
                </div>
                <div style={{ fontSize: 11, color: C.slate, marginTop: 2 }}>
                  {g.executed_verifications}/{g.declared_verifications} verifications captured
                  {g.pickup_seal_missing && " · pickup seal missing"}
                  {g.dropoff_seal_missing && " · dropoff seal missing"}
                </div>
              </div>
              <ArrowRight size={14} color={C.slate} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// VA Ops — human-in-the-loop efficiency (funnel + per-VA leaderboard + silent drops)
// ─────────────────────────────────────────────────────────────

function VaOpsSection({ vaOps, onOpenTrip }) {
  if (!vaOps) return null;
  const f = vaOps.funnel || {};
  const per = vaOps.per_va || [];
  const drops = vaOps.silent_drops || [];
  const sla = vaOps.sla_seconds || 90;

  const funnelStages = [
    { key: "offers_received",  label: "Offers received",  value: f.offers_received || 0,  color: "#64748B" },
    { key: "offers_ingested",  label: "Ingested",         value: f.offers_ingested || 0,  color: "#0EA5E9" },
    { key: "trips_placed",     label: "Placed w/ provider", value: f.trips_placed || 0,   color: "#0F766E" },
    { key: "trips_completed",  label: "Completed",         value: f.trips_completed || 0, color: C.green },
    { key: "trips_dropped",    label: "Silently dropped",  value: f.trips_dropped || 0,   color: "#B91C1C" },
  ];
  const maxVal = Math.max(1, ...funnelStages.map(s => s.value));

  return (
    <div data-testid="trust-ops-va-ops" style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <ChartLineUp size={16} weight="bold" color={C.forest} />
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>VA Ops · human-in-the-loop</div>
        <div style={{ fontSize: 11, color: C.slateDim }}>· last {vaOps.window_days ?? 7} days · SLA {sla}s to decision</div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: C.slate, fontWeight: 700 }}>
          Placement rate: <span style={{ color: (f.placement_rate ?? 0) >= 80 ? C.green : C.amber }}>{(f.placement_rate ?? 0).toFixed(1)}%</span>
        </div>
      </div>

      {/* Funnel bars */}
      <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 60px", gap: 8, alignItems: "center", marginBottom: 16 }} data-testid="va-ops-funnel">
        {funnelStages.map(s => (
          <React.Fragment key={s.key}>
            <div style={{ fontSize: 12, color: C.slate, fontWeight: 600 }}>{s.label}</div>
            <div style={{ height: 20, background: "#F1F5F9", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(s.value / maxVal) * 100}%`, background: s.color, transition: "width 0.4s" }} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.value}</div>
          </React.Fragment>
        ))}
      </div>

      {/* Per-VA leaderboard */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: C.slate, fontWeight: 700, marginBottom: 6 }}>
          Per-VA leaderboard
        </div>
        {per.length === 0 ? (
          <div style={{ fontSize: 12, color: C.slateDim, fontStyle: "italic" }}>No VA decisions recorded in the window.</div>
        ) : (
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="va-ops-leaderboard">
            <thead>
              <tr style={{ background: C.bone, color: C.slate }}>
                <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700 }}>VA</th>
                <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Assigns</th>
                <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Rejects</th>
                <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Median</th>
                <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Within SLA</th>
              </tr>
            </thead>
            <tbody>
              {per.map((v, i) => (
                <tr key={v.va} style={{ borderTop: `1px solid ${C.cardBorder}` }} data-testid={`va-ops-row-${i}`}>
                  <td style={{ padding: "6px 8px", color: C.ink, fontWeight: 600 }}>
                    {v.va === "auto-selector" ? "🤖 auto-selector" : v.va}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>{v.assigns || 0}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: (v.rejects || 0) > 0 ? C.amber : C.slate }}>{v.rejects || 0}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: (v.median_time_to_decision_s || 0) > sla ? C.red : C.slate }}>
                    {v.median_time_to_decision_s != null ? `${v.median_time_to_decision_s}s` : "—"}
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: (v.within_sla_pct || 0) >= 80 ? C.green : C.amber }}>
                    {v.within_sla_pct != null ? `${v.within_sla_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Silent drops list — the real revenue leak */}
      {drops.length > 0 && (
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#B91C1C", fontWeight: 700, marginBottom: 6 }}>
            Silent drops · {drops.length} offer{drops.length === 1 ? "" : "s"} expired without a VA decision
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {drops.slice(0, 6).map((d) => (
              <button
                key={d.trip_id}
                type="button"
                onClick={() => onOpenTrip?.(d.trip_id)}
                data-testid={`va-ops-drop-${d.trip_id}`}
                style={{
                  textAlign: "left", background: "#FEF2F2", border: "1px solid #FCA5A5",
                  borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8, color: C.ink,
                }}
              >
                <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#B91C1C" }}>{d.trip_id}</span>
                <span style={{ color: C.slate }}>· {d.status} · {d.broker?.name || d.kaizen_offer_id || "—"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function IncidentsFeed({ incidents, onOpenEntity }) {
  const rows = incidents?.events || [];
  return (
    <div data-testid="trust-ops-incidents" style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Broadcast size={16} weight="bold" color={C.brandblue} />
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Trust incidents feed</div>
        <div style={{ fontSize: 11, color: C.slateDim }}>· latest {rows.length}</div>
      </div>
      {rows.length === 0 && (
        <div style={{ padding: "12px 0", fontSize: 12, color: C.slate }}>No trust events yet.</div>
      )}
      {rows.length > 0 && (
        <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map((ev, i) => {
            const sevBg = ev.severity === "high" ? "#FEF2F2" : "#F8FAFC";
            const sevDot = ev.severity === "high" ? C.red : C.brandblue;
            return (
              <button
                key={`${ev.entity_id}-${ev.at}-${i}`}
                data-testid={`trust-ops-incident-${i}`}
                onClick={() => onOpenEntity?.(ev.entity_type, ev.entity_id)}
                style={{
                  textAlign: "left", background: sevBg, border: `1px solid ${C.cardBorder}`,
                  borderRadius: 6, padding: "8px 10px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 10,
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: sevDot, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ev.event}
                  </div>
                  <div style={{ fontSize: 11, color: C.slate, marginTop: 1 }}>
                    {ev.entity_type} · {ev.entity_id} · {ev.at ? new Date(ev.at).toLocaleString() : "—"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CredentialingHealthTile({ health, onNavigate }) {
  if (!health) return null;
  const pending = health.pending_applications ?? 0;
  const stalled = health.stalled_submissions ?? 0;
  const hot = stalled > 0;
  return (
    <div data-testid="credentialing-health-tile" onClick={() => onNavigate?.("credentialing-hub")}
      style={{
        background: "#fff", border: `1px solid ${hot ? "#FCA5A5" : C.cardBorder}`, borderRadius: 12,
        padding: 14, marginBottom: 16, cursor: onNavigate ? "pointer" : "default",
        display: "flex", alignItems: "center", gap: 16,
      }}>
      <ClipboardText size={22} weight="bold" color={hot ? C.red : C.forest} />
      <div style={{ flex: 1, display: "flex", gap: 28 }}>
        <div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.slateDim, fontWeight: 700 }}>Pending applications</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.ink }} data-testid="credentialing-health-pending">{pending}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.slateDim, fontWeight: 700 }}>Stalled MedexOva submissions</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: hot ? C.red : C.ink }} data-testid="credentialing-health-stalled">{stalled}</div>
        </div>
      </div>
      {onNavigate && <ArrowRight size={16} color={C.slateDim} />}
    </div>
  );
}

// Phase 3 — independent-driver performance rollup, the same shape a TP sees
// for their own fleet in the KPI Suite tab, feeding into Trust Ops.
function IndependentDriverPerfTile({ leaderboard, onNavigate }) {
  const rows = (leaderboard?.drivers || []).slice(0, 5);
  return (
    <div data-testid="trust-ops-independent-perf" style={{ background: "#fff", border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ChartLineUp size={16} weight="bold" color={C.forest} />
          <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Independent drivers · performance</div>
        </div>
        {onNavigate && (
          <button onClick={() => onNavigate("fleet")} data-testid="trust-ops-independent-perf-link"
            style={{ fontSize: 11, color: C.brandblue, fontWeight: 700, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            View roster <ArrowRight size={12} />
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: C.slateDim, fontStyle: "italic" }}>No independent drivers with trip history yet.</div>
      ) : (
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="trust-ops-independent-perf-table">
          <thead>
            <tr style={{ background: C.bone, color: C.slate }}>
              <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700 }}>Driver</th>
              <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Trips</th>
              <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>On-time</th>
              <th style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Completion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} style={{ borderTop: `1px solid ${C.cardBorder}` }} data-testid={`trust-ops-independent-perf-row-${d.id}`}>
                <td style={{ padding: "6px 8px", color: C.ink, fontWeight: 600 }}>{d.name}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{d.trip_count}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{d.on_time_pct != null ? `${d.on_time_pct}%` : "—"}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{d.completion_pct != null ? `${d.completion_pct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────

export default function TrustOpsPanel({ onNavigate, onOpenTrip }) {
  const [overview, setOverview] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [vaOps, setVaOps] = useState(null);
  const [credHealth, setCredHealth] = useState(null);
  const [driverPerf, setDriverPerf] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      apiClient.get("/admin/trust-ops/overview"),
      apiClient.get("/admin/trust-ops/rule-packages?window_days=7"),
      apiClient.get("/admin/trust-ops/compliance-gaps?limit=50"),
      apiClient.get("/admin/trust-ops/incidents?limit=100"),
      apiClient.get("/admin/va-ops/snapshot?window_days=7"),
      apiClient.get("/tpt/credentialing/health-rollup"),
      apiClient.get("/tpt/driver-leaderboard"),
    ]);
    let failed = 0;
    if (results[0].status === "fulfilled") setOverview(results[0].value.data); else failed++;
    if (results[1].status === "fulfilled") setTelemetry(results[1].value.data); else failed++;
    if (results[2].status === "fulfilled") setGaps(results[2].value.data); else failed++;
    if (results[3].status === "fulfilled") setIncidents(results[3].value.data); else failed++;
    if (results[4].status === "fulfilled") setVaOps(results[4].value.data); else failed++;
    if (results[5].status === "fulfilled") setCredHealth(results[5].value.data); else failed++;
    if (results[6].status === "fulfilled") setDriverPerf(results[6].value.data); else failed++;
    if (failed === results.length) {
      const detail = results.find(r => r.status === "rejected")?.reason?.response?.data?.detail;
      setErr(detail || "Trust Ops feed unavailable.");
    } else {
      setErr(null);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const openTrip = (tripId) => {
    if (onOpenTrip) {
      onOpenTrip(tripId);
    } else if (onNavigate) {
      onNavigate("live-ops");
    }
  };

  const openEntity = (type, id) => {
    if (type === "trip" && id) openTrip(id);
    else if (type === "driver" && onNavigate) onNavigate("fleet");
  };

  return (
    <div data-testid="trust-ops-panel" style={{ maxWidth: 1400 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.forest, fontWeight: 800, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
          <Sparkle size={12} weight="fill" /> Trust Operations Center
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
          The Verified Transportation Infrastructure, live.
        </div>
      </div>

      {err && (
        <div data-testid="trust-ops-error" style={{ background: "#FEF2F2", border: `1px solid #FCA5A5`, color: C.red, padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
          {err}
        </div>
      )}

      <TrustScoreHero overview={overview} />
      <CredentialingHealthTile health={credHealth} onNavigate={onNavigate} />
      <IndependentDriverPerfTile leaderboard={driverPerf} onNavigate={onNavigate} />
      <SubScoreGrid overview={overview} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <RulePackageHeatmap telemetry={telemetry} />
          <VaOpsSection vaOps={vaOps} onOpenTrip={openTrip} />
          <ComplianceGapsList gaps={gaps} onOpenTrip={openTrip} />
        </div>
        <div>
          <IncidentsFeed incidents={incidents} onOpenEntity={openEntity} />
        </div>
      </div>
    </div>
  );
}
